import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/modules/auth/stores/authStore';
import { api } from '@/services/api';
import { config } from '@/config/env';

interface Topic {
  topicId: string;
  title: string;
  emoji: string;
  category: string;
  difficulty: number;
  status: 'active' | 'inactive';
  stats: {
    totalDebates: number;
    proWins: number;
    conWins: number;
    draws: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface TopicFormData {
  title: string;
  emoji: string;
  category: string;
  difficulty: number;
}

// 난이도 정보
const DIFFICULTY_OPTIONS = [
  { value: 1, label: '입문', emoji: '🌱', description: '가벼운 일상 주제 (브론즈)', color: 'text-green-400' },
  { value: 2, label: '초급', emoji: '🌿', description: '쉬운 논쟁 주제 (실버)', color: 'text-blue-400' },
  { value: 3, label: '중급', emoji: '🌳', description: '적당한 논쟁 (골드~플래티넘)', color: 'text-yellow-400' },
  { value: 4, label: '고급', emoji: '🔥', description: '심도있는 논쟁 (다이아~마스터)', color: 'text-orange-400' },
  { value: 5, label: '전문가', emoji: '💎', description: '전문적 논쟁 (그랜드마스터)', color: 'text-purple-400' },
];

interface Feedback {
  id: string;
  type: 'bug' | 'suggestion' | 'other';
  title: string;
  description: string;
  email?: string;
  deviceInfo?: string;
  url?: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  createdAt: string;
  updatedAt: string;
  adminNote?: string;
}

type TabType = 'topics' | 'feedback';

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated } = useAuthStore();
  
  // 탭 상태
  const [activeTab, setActiveTab] = useState<TabType>('topics');
  
  const [topics, setTopics] = useState<Topic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 피드백 상태
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved' | 'closed'>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [adminNote, setAdminNote] = useState('');
  
  // 모달 상태
  const [showModal, setShowModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [formData, setFormData] = useState<TopicFormData>({
    title: '',
    emoji: '💬',
    category: 'general',
    difficulty: 1,
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // 난이도 필터
  const [difficultyFilter, setDifficultyFilter] = useState<number | 'all'>('all');

  // 권한 체크
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/lobby');
      return;
    }
  }, [isAuthenticated, isAdmin, navigate]);

  // 주제 목록 로드
  useEffect(() => {
    loadTopics();
  }, []);

  // 피드백 목록 로드
  useEffect(() => {
    if (activeTab === 'feedback') {
      loadFeedbacks();
    }
  }, [activeTab]);

  const loadTopics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{ topics: Topic[] }>('/admin/topics');
      console.log('[Admin] Topics response:', response);
      setTopics(response.topics || []);
    } catch (err) {
      console.error('Failed to load topics:', err);
      setError('주제 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 피드백 목록 로드
  const loadFeedbacks = async () => {
    setFeedbackLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/feedback`, {
        headers: {
          'x-admin-key': 'debate-admin-2024',
        },
      });
      const data = await response.json();
      setFeedbacks(data.feedbacks || []);
    } catch (err) {
      console.error('Failed to load feedbacks:', err);
      setError('피드백 목록을 불러오는데 실패했습니다.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // 피드백 상태 업데이트
  const updateFeedbackStatus = async (id: string, status: Feedback['status'], note?: string) => {
    try {
      await fetch(`${config.apiUrl}/feedback`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': 'debate-admin-2024',
        },
        body: JSON.stringify({ id, status, note }),
      });
      await loadFeedbacks();
      setSelectedFeedback(null);
      setAdminNote('');
    } catch (err) {
      console.error('Failed to update feedback:', err);
      setError('피드백 상태 업데이트에 실패했습니다.');
    }
  };

  // 주제 생성/수정
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setIsSaving(true);
    try {
      if (editingTopic) {
        // 수정
        await api.put(`/admin/topics/${editingTopic.topicId}`, formData);
      } else {
        // 생성
        await api.post('/admin/topics', formData);
      }
      await loadTopics();
      closeModal();
    } catch (err) {
      console.error('Failed to save topic:', err);
      setError('주제 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // 주제 상태 토글 (활성/비활성)
  const toggleTopicStatus = async (topic: Topic) => {
    try {
      await api.put(`/admin/topics/${topic.topicId}`, {
        status: topic.status === 'active' ? 'inactive' : 'active',
      });
      await loadTopics();
    } catch (err) {
      console.error('Failed to toggle topic status:', err);
      setError('상태 변경에 실패했습니다.');
    }
  };

  // 주제 삭제
  const deleteTopic = async (topicId: string) => {
    if (!confirm('정말로 이 주제를 삭제하시겠습니까?')) return;
    
    try {
      await api.delete(`/admin/topics/${topicId}`);
      await loadTopics();
    } catch (err) {
      console.error('Failed to delete topic:', err);
      setError('주제 삭제에 실패했습니다.');
    }
  };

  // 모달 열기 (새 주제)
  const openNewTopicModal = () => {
    setEditingTopic(null);
    setFormData({ title: '', emoji: '💬', category: 'general', difficulty: 1 });
    setShowModal(true);
  };

  // 모달 열기 (수정)
  const openEditModal = (topic: Topic) => {
    setEditingTopic(topic);
    setFormData({
      title: topic.title,
      emoji: topic.emoji,
      category: topic.category,
      difficulty: topic.difficulty || 1,
    });
    setShowModal(true);
  };

  // 모달 닫기
  const closeModal = () => {
    setShowModal(false);
    setEditingTopic(null);
    setFormData({ title: '', emoji: '💬', category: 'general', difficulty: 1 });
  };

  // 카테고리 옵션
  const categories = [
    { value: 'general', label: '일반' },
    { value: 'society', label: '사회' },
    { value: 'tech', label: '기술' },
    { value: 'lifestyle', label: '라이프스타일' },
    { value: 'entertainment', label: '엔터테인먼트' },
  ];

  // 이모지 옵션
  const emojis = ['💬', '🔥', '💡', '🤔', '🎯', '⚡', '🌟', '📱', '🎮', '🍕', '🚀', '🌍', '💰', '❤️', '🎭'];

  // 피드백 타입 라벨
  const feedbackTypeLabels: Record<string, { label: string; color: string }> = {
    bug: { label: '🐛 버그', color: 'text-red-400 bg-red-500/20' },
    suggestion: { label: '💡 제안', color: 'text-yellow-400 bg-yellow-500/20' },
    other: { label: '📝 기타', color: 'text-blue-400 bg-blue-500/20' },
  };

  // 피드백 상태 라벨
  const feedbackStatusLabels: Record<string, { label: string; color: string }> = {
    open: { label: '신규', color: 'text-green-400 bg-green-500/20' },
    in_progress: { label: '처리중', color: 'text-yellow-400 bg-yellow-500/20' },
    resolved: { label: '해결됨', color: 'text-blue-400 bg-blue-500/20' },
    closed: { label: '종료', color: 'text-slate-400 bg-slate-500/20' },
  };

  // 필터링된 피드백
  const filteredFeedbacks = feedbacks.filter(f => 
    feedbackFilter === 'all' ? true : f.status === feedbackFilter
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/lobby')}
              className="text-slate-400 hover:text-white"
            >
              ← 뒤로
            </button>
            <h1 className="text-xl font-bold">🔧 관리자 페이지</h1>
          </div>
          {activeTab === 'topics' && (
            <button
              onClick={openNewTopicModal}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg font-medium transition-colors"
            >
              + 새 주제 추가
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('topics')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'topics'
                ? 'bg-primary-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            📋 주제 관리
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'feedback'
                ? 'bg-primary-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            📬 피드백
            {feedbacks.filter(f => f.status === 'open').length > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {feedbacks.filter(f => f.status === 'open').length}
              </span>
            )}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-300 hover:text-white">✕</button>
          </div>
        )}

        {/* Topics Tab */}
        {activeTab === 'topics' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-slate-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-400">
                  {topics.filter(t => t.status === 'active').length}
                </p>
                <p className="text-sm text-slate-400">활성 주제</p>
              </div>
              <div className="bg-slate-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-slate-400">
                  {topics.filter(t => t.status === 'inactive').length}
                </p>
                <p className="text-sm text-slate-400">비활성 주제</p>
              </div>
              <div className="bg-slate-800 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-primary-400">
                  {topics.length}
                </p>
                <p className="text-sm text-slate-400">전체 주제</p>
              </div>
            </div>

            {/* Difficulty Filter */}
            <div className="flex gap-2 mb-6 flex-wrap">
              <button
                onClick={() => setDifficultyFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  difficultyFilter === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                }`}
              >
                전체
              </button>
              {DIFFICULTY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDifficultyFilter(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    difficultyFilter === opt.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                  }`}
                >
                  {opt.emoji} {opt.label} ({topics.filter(t => (t.difficulty || 1) === opt.value).length})
                </button>
              ))}
            </div>

            {/* Topic List */}
            {isLoading ? (
              <div className="text-center py-12 text-slate-400">
                로딩 중...
              </div>
            ) : topics.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-400 mb-4">등록된 주제가 없습니다.</p>
                <button
                  onClick={openNewTopicModal}
                  className="text-primary-400 hover:text-primary-300"
                >
                  첫 번째 주제를 추가해보세요
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {topics
                  .filter(t => difficultyFilter === 'all' || (t.difficulty || 1) === difficultyFilter)
                  .map((topic) => (
                  <div
                    key={topic.topicId}
                    className={`bg-slate-800 rounded-xl p-4 border ${
                      topic.status === 'active' 
                        ? 'border-green-500/30' 
                        : 'border-slate-700 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{topic.emoji}</span>
                          <h3 className="font-medium">{topic.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            topic.status === 'active'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-600 text-slate-400'
                          }`}>
                            {topic.status === 'active' ? '활성' : '비활성'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            DIFFICULTY_OPTIONS.find(d => d.value === (topic.difficulty || 1))?.color || 'text-slate-400'
                          } bg-slate-700`}>
                            {DIFFICULTY_OPTIONS.find(d => d.value === (topic.difficulty || 1))?.emoji}{' '}
                            {DIFFICULTY_OPTIONS.find(d => d.value === (topic.difficulty || 1))?.label || '입문'}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-sm text-slate-400">
                          <span>📁 {categories.find(c => c.value === topic.category)?.label || topic.category}</span>
                          <span>🎯 토론 {topic.stats.totalDebates}회</span>
                          <span className="text-green-400">👍 {topic.stats.proWins}</span>
                          <span className="text-red-400">👎 {topic.stats.conWins}</span>
                          <span className="text-yellow-400">🤝 {topic.stats.draws}</span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleTopicStatus(topic)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            topic.status === 'active'
                              ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                              : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                          }`}
                        >
                          {topic.status === 'active' ? '비활성화' : '활성화'}
                        </button>
                        <button
                          onClick={() => openEditModal(topic)}
                          className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => deleteTopic(topic.topicId)}
                          className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <>
            {/* Feedback Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <button
                onClick={() => setFeedbackFilter('all')}
                className={`bg-slate-800 rounded-xl p-4 text-center transition-colors ${
                  feedbackFilter === 'all' ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                <p className="text-2xl font-bold text-primary-400">{feedbacks.length}</p>
                <p className="text-sm text-slate-400">전체</p>
              </button>
              <button
                onClick={() => setFeedbackFilter('open')}
                className={`bg-slate-800 rounded-xl p-4 text-center transition-colors ${
                  feedbackFilter === 'open' ? 'ring-2 ring-green-500' : ''
                }`}
              >
                <p className="text-2xl font-bold text-green-400">
                  {feedbacks.filter(f => f.status === 'open').length}
                </p>
                <p className="text-sm text-slate-400">신규</p>
              </button>
              <button
                onClick={() => setFeedbackFilter('in_progress')}
                className={`bg-slate-800 rounded-xl p-4 text-center transition-colors ${
                  feedbackFilter === 'in_progress' ? 'ring-2 ring-yellow-500' : ''
                }`}
              >
                <p className="text-2xl font-bold text-yellow-400">
                  {feedbacks.filter(f => f.status === 'in_progress').length}
                </p>
                <p className="text-sm text-slate-400">처리중</p>
              </button>
              <button
                onClick={() => setFeedbackFilter('resolved')}
                className={`bg-slate-800 rounded-xl p-4 text-center transition-colors ${
                  feedbackFilter === 'resolved' ? 'ring-2 ring-slate-500' : ''
                }`}
              >
                <p className="text-2xl font-bold text-slate-400">
                  {feedbacks.filter(f => f.status === 'resolved').length}
                </p>
                <p className="text-sm text-slate-400">해결됨</p>
              </button>
            </div>

            {/* Feedback List */}
            {feedbackLoading ? (
              <div className="text-center py-12 text-slate-400">
                로딩 중...
              </div>
            ) : filteredFeedbacks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-400">
                  {feedbackFilter === 'all' 
                    ? '아직 받은 피드백이 없습니다.' 
                    : `${feedbackStatusLabels[feedbackFilter].label} 상태의 피드백이 없습니다.`}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFeedbacks.map((feedback) => (
                  <div
                    key={feedback.id}
                    className="bg-slate-800 rounded-xl p-4 border border-slate-700 hover:border-slate-600 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedFeedback(feedback);
                      setAdminNote(feedback.adminNote || '');
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${feedbackTypeLabels[feedback.type].color}`}>
                            {feedbackTypeLabels[feedback.type].label}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${feedbackStatusLabels[feedback.status].color}`}>
                            {feedbackStatusLabels[feedback.status].label}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(feedback.createdAt).toLocaleString('ko-KR')}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-200">{feedback.title}</p>
                        <p className="text-sm text-slate-400 line-clamp-2">{feedback.description}</p>
                        {feedback.email && (
                          <p className="text-xs text-slate-500 mt-1">📧 {feedback.email}</p>
                        )}
                      </div>
                      <div className="text-slate-500">→</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">
              {editingTopic ? '주제 수정' : '새 주제 추가'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 이모지 선택 */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">이모지</label>
                <div className="flex flex-wrap gap-2">
                  {emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData({ ...formData, emoji })}
                      className={`p-2 text-xl rounded-lg transition-colors ${
                        formData.emoji === emoji
                          ? 'bg-primary-500'
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              {/* 제목 */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">주제</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="예: 원격 근무는 사무실 근무보다 효율적이다"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500"
                  required
                />
              </div>

              {/* 카테고리 */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">카테고리</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 난이도 선택 */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">난이도 (티어별 매칭)</label>
                <div className="grid grid-cols-5 gap-2">
                  {DIFFICULTY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, difficulty: opt.value })}
                      className={`p-2 rounded-lg text-center transition-colors ${
                        formData.difficulty === opt.value
                          ? 'bg-primary-500 ring-2 ring-primary-400'
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      <div className="text-lg">{opt.emoji}</div>
                      <div className="text-xs">{opt.label}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {DIFFICULTY_OPTIONS.find(d => d.value === formData.difficulty)?.description}
                </p>
              </div>

              {/* 미리보기 */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-sm text-slate-400 mb-2">미리보기</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{formData.emoji}</span>
                  <span>{formData.title || '주제를 입력하세요'}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    DIFFICULTY_OPTIONS.find(d => d.value === formData.difficulty)?.color || 'text-slate-400'
                  } bg-slate-600`}>
                    {DIFFICULTY_OPTIONS.find(d => d.value === formData.difficulty)?.emoji}{' '}
                    {DIFFICULTY_OPTIONS.find(d => d.value === formData.difficulty)?.label}
                  </span>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !formData.title.trim()}
                  className="flex-1 px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                >
                  {isSaving ? '저장 중...' : editingTopic ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">피드백 상세</h2>
              <button
                onClick={() => {
                  setSelectedFeedback(null);
                  setAdminNote('');
                }}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Feedback Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs ${feedbackTypeLabels[selectedFeedback.type]?.color || 'bg-slate-500/20'}`}>
                  {feedbackTypeLabels[selectedFeedback.type]?.label || selectedFeedback.type}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs ${feedbackStatusLabels[selectedFeedback.status]?.color || 'bg-slate-500/20'}`}>
                  {feedbackStatusLabels[selectedFeedback.status]?.label || selectedFeedback.status}
                </span>
              </div>

              <div>
                <p className="text-xs text-slate-500 mb-1">제목</p>
                <p className="text-sm font-medium text-slate-200">{selectedFeedback.title}</p>
              </div>

              <div>
                <p className="text-xs text-slate-500 mb-1">내용</p>
                <p className="text-sm text-slate-200 bg-slate-700 rounded-lg p-3 whitespace-pre-wrap">
                  {selectedFeedback.description}
                </p>
              </div>

              {selectedFeedback.email && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">연락처</p>
                  <p className="text-sm text-slate-300">{selectedFeedback.email}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-500 mb-1">접수일시</p>
                  <p className="text-slate-300">
                    {new Date(selectedFeedback.createdAt).toLocaleString('ko-KR')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">페이지</p>
                  <p className="text-slate-300 text-xs break-all">{selectedFeedback.url || '-'}</p>
                </div>
              </div>

              {selectedFeedback.deviceInfo && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">환경정보</p>
                  <p className="text-xs text-slate-400 break-all">{selectedFeedback.deviceInfo}</p>
                </div>
              )}

              {/* Admin Note */}
              <div>
                <label className="text-xs text-slate-500 mb-1 block">관리자 메모</label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="처리 내용을 메모하세요..."
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm focus:outline-none focus:border-primary-500 resize-none"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {selectedFeedback.status === 'open' && (
                  <button
                    onClick={() => updateFeedbackStatus(selectedFeedback.id, 'in_progress', adminNote)}
                    className="flex-1 px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg text-sm transition-colors"
                  >
                    처리중으로 변경
                  </button>
                )}
                {selectedFeedback.status !== 'resolved' && selectedFeedback.status !== 'closed' && (
                  <button
                    onClick={() => updateFeedbackStatus(selectedFeedback.id, 'resolved', adminNote)}
                    className="flex-1 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm transition-colors"
                  >
                    해결됨으로 변경
                  </button>
                )}
                {(selectedFeedback.status === 'resolved' || selectedFeedback.status === 'closed') && (
                  <button
                    onClick={() => updateFeedbackStatus(selectedFeedback.id, 'open', adminNote)}
                    className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-slate-300 rounded-lg text-sm transition-colors"
                  >
                    다시 열기
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
